// Setup: the test profiles (virtual test users) and each data set's dashboard
// login — the source of the live expected values.
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserAstronaut, faPlugCircleCheck, faFloppyDisk, faWandMagicSparkles, faPlus, faTrash, faKey,
} from '@fortawesome/free-solid-svg-icons';
import { ASSISTANT, SCOPE, VARIANT } from './format';

const SCOPE_HINT = {
  advisor: 'Name des Beraters, genau wie in den Daten („RM Name“)',
  consolidation: 'Name der Konsolidierung',
  portfolio: 'Portfolio-Nr.',
};
const URL_HINT = {
  mh: 'http://ip_banking_api_mh:5002',
  efd_survista: 'http://ip_banking_api_survista:5002',
  ppi: 'http://ip_ppi_api:5004',
};

function ProfileRow({ p, data, client, flash, reload }) {
  const [draft, setDraft] = useState(null);
  const v = draft || p;
  const save = async () => {
    try {
      await client.put(`/api/admin/tests/profiles/${p.id}`, {
        label: v.label, scope_value: v.scope_value || '', active: v.active,
      });
      flash('Profil gespeichert.');
      setDraft(null);
      reload();
    } catch (e) {
      flash(e.message, true);
    }
  };
  const remove = async () => {
    if (!window.confirm(`Profil „${p.label}“ löschen?`)) return;
    try { await client.del(`/api/admin/tests/profiles/${p.id}`); reload(); } catch (e) { flash(e.message, true); }
  };
  return (
    <div className={`ts-profile ${v.active ? '' : 'off'}`}>
      <div className="ts-profile-id">
        <FontAwesomeIcon icon={faUserAstronaut} />
        <input className="ts-inline-input" value={v.label} onChange={(e) => setDraft({ ...v, label: e.target.value })} />
      </div>
      <div className="ts-profile-meta">
        <span className="ts-tag">{ASSISTANT[p.assistant]}</span>
        <span className="ts-tag soft">{p.data_set_name || p.data_source_key}</span>
        <span className="ts-tag soft">{VARIANT[p.variant] || p.variant || 'Datensatz fehlt'}</span>
        <span className="ts-tag soft">{SCOPE[p.scope_type]}</span>
      </div>
      {p.scope_type !== 'all' ? (
        <input className="ts-scope-input" value={v.scope_value || ''} placeholder={SCOPE_HINT[p.scope_type]}
          onChange={(e) => setDraft({ ...v, scope_value: e.target.value })} />
      ) : <span className="ts-muted">sieht alles</span>}
      <label className="ts-check-inline">
        <input type="checkbox" checked={!!v.active} onChange={(e) => setDraft({ ...v, active: e.target.checked })} /> aktiv
      </label>
      <div className="ts-row-actions">
        {draft && <button type="button" className="ts-btn small" onClick={save}><FontAwesomeIcon icon={faFloppyDisk} /> Speichern</button>}
        <button type="button" className="ts-icon-btn danger" title="Profil löschen" onClick={remove}><FontAwesomeIcon icon={faTrash} /></button>
      </div>
    </div>
  );
}

function NewProfile({ data, client, flash, reload }) {
  const sets = (data.data_sets || []).filter((d) => d.variant);
  const [form, setForm] = useState(null);
  if (!form) {
    return <button type="button" className="ts-btn ghost" onClick={() => setForm({ data_source_key: sets[0]?.key || '', scope_type: 'all', scope_value: '' })}>
      <FontAwesomeIcon icon={faPlus} /> Profil hinzufügen</button>;
  }
  const ds = sets.find((d) => d.key === form.data_source_key);
  const scopes = (data.scope_types || {})[ds?.variant] || ['all'];
  const add = async () => {
    try {
      const assistant = ds.model === 'ppi' ? 'ppi' : 'efd';
      await client.post('/api/admin/tests/profiles', {
        label: `${ASSISTANT[assistant]} · ${ds.name} · ${SCOPE[form.scope_type]}${form.scope_value ? ` ${form.scope_value}` : ''}`,
        assistant, data_source_key: ds.key, scope_type: form.scope_type, scope_value: form.scope_value, active: true,
      });
      setForm(null);
      reload();
    } catch (e) {
      flash(e.message, true);
    }
  };
  return (
    <div className="ts-row wrap ts-new-profile">
      <label className="ts-field"><span>Datensatz</span>
        <select value={form.data_source_key} onChange={(e) => setForm({ ...form, data_source_key: e.target.value, scope_type: 'all' })}>
          {sets.map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
        </select></label>
      <label className="ts-field"><span>Scope-Typ</span>
        <select value={form.scope_type} onChange={(e) => setForm({ ...form, scope_type: e.target.value })}>
          {scopes.map((s) => <option key={s} value={s}>{SCOPE[s]}</option>)}
        </select></label>
      {form.scope_type !== 'all' && (
        <label className="ts-field grow"><span>{SCOPE_HINT[form.scope_type]}</span>
          <input value={form.scope_value} onChange={(e) => setForm({ ...form, scope_value: e.target.value })} /></label>
      )}
      <button type="button" className="ts-btn" onClick={add} disabled={!ds}>Anlegen</button>
      <button type="button" className="ts-btn ghost" onClick={() => setForm(null)}>Abbrechen</button>
    </div>
  );
}

function DashboardCard({ ds, client, flash, reload }) {
  const [url, setUrl] = useState(ds.dashboard_url || '');
  const [user, setUser] = useState(ds.dashboard_user || '');
  const [password, setPassword] = useState('');
  const [state, setState] = useState(null);          // {ok, message}
  const dirty = url !== (ds.dashboard_url || '') || user !== (ds.dashboard_user || '') || password;
  const save = async () => {
    try {
      await client.put(`/api/admin/tests/dashboards/${encodeURIComponent(ds.key)}`,
        { url, user, ...(password ? { password } : {}) });
      setPassword('');
      flash('Dashboard gespeichert.');
      reload();
    } catch (e) {
      flash(e.message, true);
    }
  };
  const check = async () => {
    setState({ ok: null, message: 'Prüfe …' });
    try {
      setState(await client.post(`/api/admin/tests/dashboards/${encodeURIComponent(ds.key)}/check`));
    } catch (e) {
      setState({ ok: false, message: e.message });
    }
  };
  return (
    <div className="ts-dash">
      <div className="ts-dash-head">
        <strong>{ds.name}</strong>
        <span className="ts-tag soft">{VARIANT[ds.variant] || ds.variant || ds.model}</span>
        <span className={`ts-pill ${ds.dashboard_url && ds.dashboard_password_set ? 'ok' : 'off'}`}>
          {ds.dashboard_url && ds.dashboard_password_set ? 'verbunden' : 'kein Login'}
        </span>
      </div>
      <div className="ts-row wrap">
        <label className="ts-field grow"><span>API-Adresse (im Docker-Netz der Instanz)</span>
          <input value={url} placeholder={URL_HINT[ds.variant] || 'http://…'} onChange={(e) => setUrl(e.target.value)} /></label>
        <label className="ts-field"><span>Login</span>
          <input value={user} placeholder="heybro-testsuite" onChange={(e) => setUser(e.target.value)} /></label>
        <label className="ts-field"><span><FontAwesomeIcon icon={faKey} /> Passwort {ds.dashboard_password_set ? '(gespeichert)' : ''}</span>
          <input type="password" value={password} autoComplete="new-password"
            placeholder={ds.dashboard_password_set ? '•••••••• (unverändert)' : ''} onChange={(e) => setPassword(e.target.value)} /></label>
      </div>
      <div className="ts-actions">
        <button type="button" className="ts-btn small" disabled={!dirty} onClick={save}><FontAwesomeIcon icon={faFloppyDisk} /> Speichern</button>
        <button type="button" className="ts-btn small ghost" onClick={check}><FontAwesomeIcon icon={faPlugCircleCheck} /> Verbindung testen</button>
        {state && <span className={`ts-check-msg ${state.ok === true ? 'ok' : state.ok === false ? 'bad' : ''}`}>{state.message}</span>}
      </div>
    </div>
  );
}

export default function SetupView({ client, data, reload, flash }) {
  const [busy, setBusy] = useState(false);
  const defaults = async () => {
    setBusy(true);
    try {
      const r = await client.post('/api/admin/tests/profiles/defaults');
      flash(r.created ? `${r.created} Profile angelegt.` : 'Alle Standardprofile sind schon da.');
      reload();
    } catch (e) {
      flash(e.message, true);
    }
    setBusy(false);
  };
  return (
    <div className="ts-pane">
      <section className="ts-section">
        <div className="ts-section-head">
          <div>
            <h2 className="ts-h2">Testprofile</h2>
            <p className="ts-lead">Virtuelle Testnutzer: ein Datensatz und ein Scope — genau die Rechte, die ein echter Nutzer mit dieser Einstellung hätte. Sie erscheinen nirgends als Nutzer.</p>
          </div>
          <button type="button" className="ts-btn ghost" disabled={busy} onClick={defaults}>
            <FontAwesomeIcon icon={faWandMagicSparkles} /> Standardprofile anlegen
          </button>
        </div>
        <div className="ts-profiles">
          {(data.profiles || []).map((p) => <ProfileRow key={p.id} p={p} data={data} client={client} flash={flash} reload={reload} />)}
          {!(data.profiles || []).length && <div className="ts-empty">Noch keine Profile — „Standardprofile anlegen“ erzeugt eines je Datensatz und Scope-Typ.</div>}
        </div>
        <NewProfile data={data} client={client} flash={flash} reload={reload} />
      </section>
      <section className="ts-section">
        <div className="ts-section-head">
          <div>
            <h2 className="ts-h2">Dashboards</h2>
            <p className="ts-lead">Woher die erwarteten Werte live kommen: die API des Kunden-Dashboards je Datensatz, mit einem technischen Login dieses Dashboards. Das Passwort wird verschlüsselt gespeichert und nie wieder angezeigt.</p>
          </div>
        </div>
        {!data.secrets_ready && <div className="ts-warn">SECRETS_KEY fehlt auf dieser Instanz — Passwörter können nicht gespeichert werden.</div>}
        <div className="ts-dashes">
          {(data.data_sets || []).filter((d) => d.variant).map((ds) => (
            <DashboardCard key={`${ds.key}:${ds.dashboard_url}:${ds.dashboard_password_set}`} ds={ds} client={client} flash={flash} reload={reload} />
          ))}
        </div>
      </section>
    </div>
  );
}
